import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {SequenceNodeOutputData} from 'common/src/modules/decisionTree/decisionTree.types';
import {NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import _findLastIndex from 'lodash/findLastIndex';
import {SequenceCardScratchpadState} from '../../scratchpad/sequenceCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';

const addRegularOutputInputSchema = z.object({
  name: z.string().describe('The name of the output'),
  maxRepeats: z
    .number()
    .int()
    .positive()
    .min(1)
    .describe(
      'How many times a user sees this output before moving to the next one in the sequence (frequency cap, minimum 1)',
    ),
});

export default function addRegularOutputTool() {
  async function execute(
    params: ToolExecuteParams<typeof addRegularOutputInputSchema, SequenceCardScratchpadState>,
  ) {
    const {name, maxRepeats} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad<SequenceCardScratchpadState>(
      agentScratchpad,
      NodeType.SEQUENCE,
    );

    const nextId =
      validatedScratchpad.card.outputs.length > 0
        ? Math.max(...validatedScratchpad.card.outputs.map((o) => o.id), 0) + 1
        : 1;

    const newOutput: SequenceNodeOutputData = {
      id: nextId,
      nodeId: NO_NODE_ID,
      name: name ?? `Output ${nextId}`,
      maxRepeats,
      type: OutputType.Regular,
    };

    const lastRegularIndex = _findLastIndex(
      validatedScratchpad.card.outputs,
      (output) => output.type === OutputType.Regular,
    );

    if (lastRegularIndex === -1) {
      validatedScratchpad.card.outputs.unshift(newOutput);
    } else {
      validatedScratchpad.card.outputs.splice(lastRegularIndex + 1, 0, newOutput);
    }

    return toolResult(`Regular output "${name}" added successfully (frequency cap: ${maxRepeats})`);
  }

  return tool<typeof addRegularOutputInputSchema, SequenceCardScratchpadState>({
    name: V2CardAgentToolName.AddRegularOutput,
    displayName: 'Adding regular output',
    description:
      'Add a regular output (a step in the sequence). Each output has a frequency cap (maxRepeats) controlling how many times a user sees it before advancing to the next.',
    inputSchema: addRegularOutputInputSchema,
    execute,
  });
}
